import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle2, CreditCard, ExternalLink } from "lucide-react";

export function MercuryConnectedBadge({ className }: { className?: string }) {
  const { data, isLoading } = useQuery<{ connected: boolean }>({
    queryKey: ["/api/app/mercury-connection/summary"],
  });

  if (isLoading || !data?.connected) return null;

  return (
    <Link
      href="/app/financials"
      className={`inline-flex items-center gap-1.5 rounded-full border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-950/40 px-2.5 py-1 text-xs font-medium text-green-700 dark:text-green-300 hover:bg-green-100 dark:hover:bg-green-900/40 transition-colors${className ? ` ${className}` : ""}`}
      data-testid="badge-mercury-connected"
      title="Mercury is linked. Click to manage in Financials."
    >
      <CheckCircle2 className="h-3.5 w-3.5" />
      Mercury Connected
    </Link>
  );
}

export function MercuryRecommendationCard({ className }: { className?: string }) {
  const { data, isLoading } = useQuery<{ connected: boolean }>({
    queryKey: ["/api/app/mercury-connection/summary"],
  });

  if (isLoading || data?.connected) return null;

  return (
    <Card className={`border-blue-200 dark:border-blue-800 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950/40 dark:to-indigo-950/40${className ? ` ${className}` : ""}`} data-testid="card-mercury-recommendation">
      <CardContent className="p-5">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <CreditCard className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              <h3 className="font-semibold text-gray-900 dark:text-gray-100 text-base">Recommended: Mercury Bank</h3>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
              Easily create individual virtual credit cards per property — keep expenses organized and stay in control across your entire portfolio.
            </p>
            <p className="text-sm text-blue-700 dark:text-blue-300 font-medium">
              Earn a $250 bonus when you deposit $10,000 within 90 days of opening your account.
            </p>
          </div>
          <div className="shrink-0">
            <Button
              asChild
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="button-mercury-cta"
            >
              <a href="https://mercury.com/r/padpulse-llc" target="_blank" rel="noopener noreferrer">
                Create Account Now
                <ExternalLink className="h-4 w-4 ml-2" />
              </a>
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
